import type {
  ActivityIngredientResponse,
  AlternativeActivityIngredientResponseWithStaticInfo,
  AlternativeFerryIngredientResponseWithStaticInfo,
  AlternativeFlightIngredientResponseWithStaticInfo,
  AlternativeTransportIngredientResponseWithStaticInfo,
  AvailableActivityIngredientResponse,
  AvailableActivityIngredientResponseWithStaticInfo,
  AvailableFerryIngredientResponse,
  AvailableFerryIngredientResponseWithStaticInfo,
  AvailableFlightIngredientResponse,
  AvailableFlightIngredientResponseWithStaticInfo,
  AvailableHotelIngredientResponse,
  AvailableHotelIngredientResponseWithStaticInfo,
  FerryIngredientResponse,
  FlightIngredientResponse,
  FlightIngredientResponseWithStaticInfo,
  HotelIngredientResponse,
  HotelIngredientResponseWithStaticInfo,
  ItineraryIngredientResponse,
  ItineraryMainIngredientResponseWithStaticInfo,
  OwnStayIngredientResponse,
  OwnStayIngredientResponseWithStaticInfo,
  OwnTransportIngredientResponse,
  OwnTransportIngredientResponseWithStaticInfo,
  TransportIngredientResponseWithStaticInfo,
} from '@libs/trip-planner-interfaces';
import { IngredientType } from '@libs/trip-planner-interfaces';

export function isTransportIngredientResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is FlightIngredientResponse | FerryIngredientResponse {
  return (
    isFlightIngredientResponse(ingredientResponse) ||
    isFerryIngredientResponse(ingredientResponse)
  );
}

export function isAvailableTransportIngredientResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is
  AvailableFlightIngredientResponse | AvailableFerryIngredientResponse {
  return (
    isTransportIngredientResponse(ingredientResponse) &&
    ingredientResponse.isAvailable
  );
}

export function isAvailableTransportIngredientResponseWithStaticInfo(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is
  | AvailableFlightIngredientResponseWithStaticInfo
  | AvailableFerryIngredientResponseWithStaticInfo {
  return (
    isTransportIngredientResponse(ingredientResponse) &&
    ingredientResponse.isAvailable
  );
}

export function isAlternativeTransportIngredientResponseWithStaticInfo(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is AlternativeTransportIngredientResponseWithStaticInfo {
  return (
    isTransportIngredientResponse(ingredientResponse) &&
    !isAvailableTransportIngredientResponseWithStaticInfo(ingredientResponse) &&
    !!ingredientResponse.alternativeSuggestion
  );
}

export function isAlternativeFlightIngredientResponseWithStaticInfo(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is AlternativeFlightIngredientResponseWithStaticInfo {
  return (
    isTransportIngredientResponse(ingredientResponse) &&
    ingredientResponse.ingredientType === IngredientType.Flight &&
    !isAvailableTransportIngredientResponseWithStaticInfo(ingredientResponse) &&
    !!ingredientResponse.alternativeSuggestion
  );
}

export function isAlternativeFerryIngredientResponseWithStaticInfo(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is AlternativeFerryIngredientResponseWithStaticInfo {
  return (
    isTransportIngredientResponse(ingredientResponse) &&
    ingredientResponse.ingredientType === IngredientType.Ferry &&
    !isAvailableTransportIngredientResponseWithStaticInfo(ingredientResponse) &&
    !!ingredientResponse.alternativeSuggestion
  );
}

export function isOwnStayIngredientResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is OwnStayIngredientResponse {
  return ingredientResponse.ingredientType === IngredientType.OwnStay;
}

export function isOwnStayIngredientResponseWithStaticInfo(
  ingredientResponse: ItineraryMainIngredientResponseWithStaticInfo
): ingredientResponse is OwnStayIngredientResponseWithStaticInfo {
  return ingredientResponse.ingredientType === IngredientType.OwnStay;
}

export function isOwnTransportIngredientResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is OwnTransportIngredientResponse {
  return ingredientResponse.ingredientType === IngredientType.OwnTransport;
}

export function isOwnTransportIngredientResponseWithStaticInfo(
  ingredientResponse: ItineraryMainIngredientResponseWithStaticInfo
): ingredientResponse is OwnTransportIngredientResponseWithStaticInfo {
  return ingredientResponse.ingredientType === IngredientType.OwnTransport;
}

export function isHotelIngredientResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is HotelIngredientResponse {
  return ingredientResponse.ingredientType === IngredientType.Hotel;
}

export function isHotelIngredientResponseWithStaticInfo(
  ingredientResponse: ItineraryMainIngredientResponseWithStaticInfo
): ingredientResponse is HotelIngredientResponseWithStaticInfo {
  return ingredientResponse.ingredientType === IngredientType.Hotel;
}

export function isStayIngredientResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is OwnStayIngredientResponse | HotelIngredientResponse {
  return (
    isHotelIngredientResponse(ingredientResponse) ||
    isOwnStayIngredientResponse(ingredientResponse)
  );
}

export function isStayIngredientResponseWithStaticInfo(
  ingredientResponse: ItineraryMainIngredientResponseWithStaticInfo
): ingredientResponse is
  | OwnStayIngredientResponseWithStaticInfo
  | HotelIngredientResponseWithStaticInfo {
  return (
    isHotelIngredientResponseWithStaticInfo(ingredientResponse) ||
    isOwnStayIngredientResponseWithStaticInfo(ingredientResponse)
  );
}

// TODO implement unit tests for those new functions below
export function isTransportIngredientResponseWithStaticInfo(
  ingredientResponse: ItineraryMainIngredientResponseWithStaticInfo
): ingredientResponse is TransportIngredientResponseWithStaticInfo {
  return (
    isFlightIngredientResponseWithStaticInfo(ingredientResponse) ||
    isFerryIngredientResponseWithStaticInfo(ingredientResponse)
  );
}

export function isFlightIngredientResponseWithStaticInfo(
  ingredientResponse: ItineraryMainIngredientResponseWithStaticInfo
): ingredientResponse is FlightIngredientResponseWithStaticInfo {
  return ingredientResponse.ingredientType === IngredientType.Flight;
}

export function isFerryIngredientResponseWithStaticInfo(
  ingredientResponse: ItineraryMainIngredientResponseWithStaticInfo
): ingredientResponse is FlightIngredientResponseWithStaticInfo {
  return ingredientResponse.ingredientType === IngredientType.Ferry;
}

export function isTrasnportIngredientResponseWithStaticInfo(
  ingredientResponse: ItineraryMainIngredientResponseWithStaticInfo
): ingredientResponse is TransportIngredientResponseWithStaticInfo {
  return (
    ingredientResponse.ingredientType === IngredientType.Flight ||
    ingredientResponse.ingredientType === IngredientType.Ferry
  );
}

export function isAvailableHotelIngredientResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is AvailableHotelIngredientResponse {
  return (
    ingredientResponse.ingredientType === IngredientType.Hotel &&
    ingredientResponse.isAvailable
  );
}

export function isAvailableHotelIngredientResponseWithStaticInfo(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is AvailableHotelIngredientResponseWithStaticInfo {
  return (
    ingredientResponse.ingredientType === IngredientType.Hotel &&
    ingredientResponse.isAvailable
  );
}

export function isFlightIngredientResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is FlightIngredientResponse {
  return ingredientResponse.ingredientType === IngredientType.Flight;
}

export function isAvailableFlightIngredientResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is AvailableFlightIngredientResponse {
  return (
    isFlightIngredientResponse(ingredientResponse) &&
    !!ingredientResponse.totalPrice
  );
}

export function isFerryIngredientResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is FerryIngredientResponse {
  return ingredientResponse.ingredientType === IngredientType.Ferry;
}

export function isAvailableFerryIngredientResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is AvailableFerryIngredientResponse {
  return (
    ingredientResponse.ingredientType === IngredientType.Ferry &&
    ingredientResponse.isAvailable
  );
}

export function isActivityIngredientResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is ActivityIngredientResponse {
  return ingredientResponse.ingredientType === IngredientType.Activity;
}

export function isAvailableActivityIngredientResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is AvailableActivityIngredientResponse {
  return (
    ingredientResponse.ingredientType === IngredientType.Activity &&
    ingredientResponse.isAvailable
  );
}

export function isAvailableActivityIngredientWithStaticInfoResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is AvailableActivityIngredientResponseWithStaticInfo {
  return (
    'staticInfo' in ingredientResponse &&
    ingredientResponse.ingredientType === IngredientType.Activity &&
    ingredientResponse.isAvailable
  );
}

export function isAlternativeActivityIngredientWithStaticInfoResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is AlternativeActivityIngredientResponseWithStaticInfo {
  return (
    'staticInfo' in ingredientResponse &&
    ingredientResponse.ingredientType === IngredientType.Activity &&
    !!ingredientResponse.alternativeSuggestion
  );
}
